import { useMemo, useCallback, useState } from "react";
import { useIntl } from "@leagueplatform/locales";
import { useForm } from "@leagueplatform/web-common";
import { StackLayout } from "@leagueplatform/genesis-core";
import { EnableTextNotificationsModal } from "components/modals/enable-text-notifications/enable-text-notifications-modal.component";
import { CommunicationsFormContent } from "components/forms/communications-section/communications-form-content.component";
import { FormContainer } from "components/forms/form-container/form-container.component";
import { getPreferenceInfo } from "hooks/queries/use-get-communication-preferences.hook";
import type { TransformedCommunicationPreferencesData } from "types/types";
import { PREFERENCE_ID_MAP } from "common/constants/api";

export interface CommunicationsSectionProps {
  data: TransformedCommunicationPreferencesData | undefined;
  mobilePhoneNumber?: string;
  onUpdatePreferences: (
    preferences: CommunicationPreferencesValues
  ) => Promise<void>;
}

export type CommunicationPreferencesValues = {
  emailNotification: boolean;
  textNotification: boolean;
  paperReports: boolean;
  mobilePhoneNumber?: string;
};

export const CommunicationsSection = ({
  data,
  mobilePhoneNumber,
  onUpdatePreferences,
}: CommunicationsSectionProps) => {
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [
    isEnableTextNotificationsModalOpen,
    setIsEnableTextNotificationsModalOpen,
  ] = useState(false);

  const textNotificationPref = useMemo(
    () => getPreferenceInfo(data, PREFERENCE_ID_MAP.text),
    [data]
  );

  const paperReportsPref = useMemo(
    () => getPreferenceInfo(data, PREFERENCE_ID_MAP.paperReport),
    [data]
  );

  const emailNotificationPref = useMemo(
    () => getPreferenceInfo(data, PREFERENCE_ID_MAP.email),
    [data]
  );

  const defaultValues = useMemo<CommunicationPreferencesValues>(
    () => ({
      emailNotification: emailNotificationPref?.optIn ?? false,
      textNotification: textNotificationPref?.optIn ?? false,
      paperReports: paperReportsPref?.optIn ?? false,
      mobilePhoneNumber: mobilePhoneNumber ?? "",
    }),
    [
      emailNotificationPref,
      textNotificationPref,
      paperReportsPref,
      mobilePhoneNumber,
    ]
  );

  const formMethods = useForm<CommunicationPreferencesValues>({
    defaultValues,
  });

  const getYesNoValue = useCallback(
    (value?: boolean): string => {
      if (value === undefined) return "";
      return value
        ? formatMessage({ id: "STR_YES" })
        : formatMessage({ id: "STR_NO" });
    },
    [formatMessage]
  );

  const profileFieldValues = useMemo(() => {
    if (!data) return [];

    const values = [
      {
        label: formatMessage({ id: "STR_EMAIL" }),
        value: getYesNoValue(emailNotificationPref?.optIn),
        isLocked: false,
      },
    ];

    if (textNotificationPref) {
      values.push({
        label: formatMessage({ id: "STR_TEXT" }),
        value: getYesNoValue(textNotificationPref?.optIn),
        isLocked: false,
      });
    }

    if (paperReportsPref) {
      values.push({
        label: formatMessage({ id: "STR_PAPER_REPORT" }),
        value: getYesNoValue(paperReportsPref?.optIn),
        isLocked: false,
      });
    }

    return values;
  }, [
    data,
    emailNotificationPref,
    textNotificationPref,
    paperReportsPref,
    formatMessage,
    getYesNoValue,
  ]);

  const handleTextNotificationsSubmit = async (phoneNumber: string) => {
    try {
      setIsLoading(true);
      formMethods.setValue("mobilePhoneNumber", phoneNumber, {
        shouldDirty: true,
      });
      formMethods.setValue("textNotification", true, { shouldDirty: true });
      setIsEnableTextNotificationsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextNotificationsCancel = () => {
    setIsEnableTextNotificationsModalOpen(false);
  };

  const handleSubmit = async (values: CommunicationPreferencesValues) => {
    try {
      setIsLoading(true);
      await onUpdatePreferences(values);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <FormContainer
        heading={formatMessage({ id: "STR_COMMUNICATION_PREFERENCES" })}
        profileFieldValues={profileFieldValues}
        formMethods={formMethods}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <StackLayout spacing="$one" horizontalAlignment="stretch">
          <CommunicationsFormContent
            data={data?.data}
            isLoading={isLoading}
            onOpenEnableTextNotificationsModal={() =>
              setIsEnableTextNotificationsModalOpen(true)
            }
            isEnableTextNotificationsModalOpen={
              isEnableTextNotificationsModalOpen
            }
          />
        </StackLayout>
      </FormContainer>
      <EnableTextNotificationsModal
        isOpen={isEnableTextNotificationsModalOpen}
        formMethods={formMethods}
        onSubmit={handleTextNotificationsSubmit}
        onCancel={handleTextNotificationsCancel}
        existingMobileNumber={mobilePhoneNumber}
        isLoading={isLoading}
      />
    </>
  );
};
